import { useMemo } from 'react';

import { graphql } from '@/__generated__';
import { ResourceNodeType } from '@/__generated__/graphql';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { useQueryStable } from '../useQueryStable';

export const lineageResourceTypesCamelCase = [
  'model',
  'macro',
  'snapshot',
  'seed',
  'source',
  'exposure',
  'test',
  'metric',
  'semanticModel',
  'savedQuery',
] as const;

export const lineageResourceTypesUnderscored = [
  'model',
  'macro',
  'snapshot',
  'seed',
  'source',
  'exposure',
  'test',
  'metric',
  'semantic_model',
  'saved_query',
] as const;

export const readonlyDiscoveryLineageResourceTypes = [
  ResourceNodeType.Model,
  ResourceNodeType.Macro,
  ResourceNodeType.Snapshot,
  ResourceNodeType.Seed,
  ResourceNodeType.Source,
  ResourceNodeType.Exposure,
  ResourceNodeType.Metric,
  ResourceNodeType.Test,
  ResourceNodeType.SemanticModel,
  ResourceNodeType.SavedQuery,
] as const;
export const discoveryLineageResourceTypes = [...readonlyDiscoveryLineageResourceTypes];

export type LineageResourceType = (typeof lineageResourceTypesCamelCase)[number];
export type DiscoveryLineageResourceType =
  (typeof readonlyDiscoveryLineageResourceTypes)[number];

export type AppliedLineageResponse = NonNullable<
  ReturnType<typeof useDiscoveryAppliedLineage>['data']
>;

export type ResponseAppliedLineageNode = NonNullable<
  AppliedLineageResponse['environment']['applied']['lineage']
>[number];

export function getLineageNodes(data: AppliedLineageResponse | undefined) {
  return data?.environment.applied.lineage;
}

/**
 * TODO: Does not yet support `Analysis` and `Metric`
 * Nate says that Analysis shouldn't be in the DAG!
 *
 * dbtProjectName is only available in dbt 1.6+
 */
export const GetLineage = graphql(/* GraphQL */ `
  query AppliedLineage(
    $environmentId: BigInt!
    $userId: BigInt
    $columnNames: [String!]
    $tags: [String!]
    $types: [ResourceNodeType!]!
    $uniqueIds: [String!]
    $select: String
    $exclude: String
  ) {
    environment(id: $environmentId, userId: $userId) {
      dbtProjectName
      applied {
        lastUpdatedAt
        lineage(
          filter: {
            columnNames: $columnNames
            tags: $tags
            types: $types
            uniqueIds: $uniqueIds
            select: $select
            exclude: $exclude
          }
        ) {
          name
          projectId
          filePath
          uniqueId
          tags
          ... on LineageNodeWithParents {
            parentIds
          }
          ... on ModelLineageNode {
            alias
            database
            schema
            version
            access
            materializationType
            group
            publicParentIds
            modelingLayer
            usageQueryCount(age: 30)
          }
          ... on SemanticModelLineageNode {
            publicParentIds
          }
          ... on LineageNodeExecutable {
            lastRunStatus
          }
          ... on LineageGraphNode {
            fqn
          }
          ... on LineageNodeTestable {
            worstTestStatus
          }
          ... on ExposureLineageNode {
            autoBiProvider
          }
        }
      }
    }
  }
`);

export const GetLineageWithoutQueryHistory = graphql(/* GraphQL */ `
  query AppliedLineageWithoutQueryHistory(
    $environmentId: BigInt!
    $userId: BigInt
    $columnNames: [String!]
    $tags: [String!]
    $types: [ResourceNodeType!]!
    $uniqueIds: [String!]
    $select: String
    $exclude: String
  ) {
    environment(id: $environmentId, userId: $userId) {
      dbtProjectName
      applied {
        lastUpdatedAt
        lineage(
          filter: {
            columnNames: $columnNames
            tags: $tags
            types: $types
            uniqueIds: $uniqueIds
            select: $select
            exclude: $exclude
          }
        ) {
          name
          projectId
          filePath
          uniqueId
          tags
          ... on LineageNodeWithParents {
            parentIds
          }
          ... on ModelLineageNode {
            alias
            database
            schema
            version
            access
            materializationType
            group
            publicParentIds
            modelingLayer
          }
          ... on SemanticModelLineageNode {
            publicParentIds
          }
          ... on LineageNodeExecutable {
            lastRunStatus
          }
          ... on LineageGraphNode {
            fqn
          }
          ... on LineageNodeTestable {
            worstTestStatus
          }
          ... on ExposureLineageNode {
            autoBiProvider
          }
        }
      }
    }
  }
`);

type LineageByEnvironmentParams = {
  environmentId: number | undefined;
  userId: number | 'loading' | null;
  columnNames?: string[] | null;
  tags?: string[] | null;
  pause?: boolean;
  types?: ResourceNodeType[];
  uniqueIds?: string[] | null;
  select?: string | null;
  exclude?: string | null;
};

export const useDiscoveryAppliedLineage = ({
  environmentId = 0,
  userId = null,
  columnNames = null,
  tags = null,
  types = discoveryLineageResourceTypes,
  uniqueIds = null,
  select = null,
  exclude = null,
  pause,
}: LineageByEnvironmentParams) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const queryChoice = hasAccountFeature('explorerQueryHistory')
    ? GetLineage
    : GetLineageWithoutQueryHistory;
  const [query] = useQueryStable({
    query: queryChoice,
    variables: {
      environmentId,
      userId: userId as number | null,
      columnNames,
      tags,
      types,
      uniqueIds,
      select: select === '' ? '*' : select,
      exclude,
    },
    pause:
      pause ||
      !environmentId ||
      uniqueIds?.length === 0 ||
      tags?.length === 0 ||
      columnNames?.length === 0 ||
      userId === 'loading',
  });

  useCacheMonitor({ applied: query.data?.environment.applied?.lastUpdatedAt });

  return useMemo(() => {
    const lineage = query.data?.environment.applied?.lineage.map((node) => ({
      ...node,
      environmentId,
    }));
    return {
      fetching: query.fetching,
      error: query.error,
      stale: query.stale,
      operation: query.operation,
      data: query.data
        ? {
            ...query.data,
            environment: {
              ...query.data.environment,
              applied: {
                ...query.data.environment.applied,
                lineage,
              },
            },
          }
        : null,
    };
  }, [
    environmentId,
    query.data,
    query.fetching,
    query.error,
    query.stale,
    query.operation,
  ]);
};
